/**
 * 설비 수량 수정 모달
 * 보유 대수 / 점검 수량을 수정하는 심플한 모달
 */

import { useState, useEffect } from "react";
import { Minus, Plus } from "lucide-react";

/** 최대 수량 제한 */
const MAX_COUNT = 999;

interface EquipmentCountEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (totalCount: number, checkCount: number) => void;
  equipmentTitle: string;
  initialTotalCount: number;
  initialCheckCount: number;
  isLoading?: boolean;
}

export function EquipmentCountEditModal({
  isOpen,
  onClose,
  onConfirm,
  equipmentTitle,
  initialTotalCount,
  initialCheckCount,
  isLoading = false,
}: EquipmentCountEditModalProps) {
  const [totalCount, setTotalCount] = useState(initialTotalCount);
  const [checkCount, setCheckCount] = useState(initialCheckCount);

  // 모달 열릴 때 초기값 설정
  useEffect(() => {
    if (isOpen) {
      setTotalCount(initialTotalCount);
      setCheckCount(initialCheckCount);
    }
  }, [isOpen, initialTotalCount, initialCheckCount]);

  // 보유 대수 변경 시 점검 수량도 함께 클램핑
  const handleTotalChange = (newTotal: number) => {
    setTotalCount(newTotal);
    if (checkCount > newTotal) {
      setCheckCount(newTotal);
    }
  };

  const handleConfirm = () => {
    onConfirm(totalCount, checkCount);
  };

  const hasChanges = totalCount !== initialTotalCount || checkCount !== initialCheckCount;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
      {/* 배경 오버레이 */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* 모달 */}
      <div className="relative mx-4 w-full max-w-sm rounded-2xl bg-white shadow-xl">
        {/* 헤더 */}
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 className="text-lg font-bold text-slate-900">수량 수정</h2>
          <p className="mt-0.5 text-sm text-slate-500">{equipmentTitle}</p>
        </div>

        {/* 입력 영역 */}
        <div className="space-y-4 p-5">
          {/* 보유 대수 */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">보유 대수</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleTotalChange(Math.max(0, totalCount - 1))}
                disabled={isLoading || totalCount <= 0}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-colors active:bg-slate-200 disabled:opacity-30"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-12 text-center text-lg font-bold tabular-nums text-slate-900">
                {totalCount}
              </span>
              <button
                onClick={() => handleTotalChange(Math.min(MAX_COUNT, totalCount + 1))}
                disabled={isLoading || totalCount >= MAX_COUNT}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-colors active:bg-slate-200 disabled:opacity-30"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* 점검 수량 */}
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-slate-700">점검 수량</span>
              {totalCount > 0 && (
                <span className="ml-2 text-xs text-slate-400">최대 {totalCount}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCheckCount(Math.max(0, checkCount - 1))}
                disabled={isLoading || checkCount <= 0}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-colors active:bg-slate-200 disabled:opacity-30"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-12 text-center text-lg font-bold tabular-nums text-slate-900">
                {checkCount}
              </span>
              <button
                onClick={() => setCheckCount(Math.min(totalCount, checkCount + 1))}
                disabled={isLoading || checkCount >= totalCount}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-colors active:bg-slate-200 disabled:opacity-30"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex gap-2 border-t border-slate-100 p-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 rounded-lg bg-slate-100 py-3 font-medium text-slate-600 transition-colors active:bg-slate-200 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={!hasChanges || isLoading}
            className="flex-1 rounded-lg bg-blue-600 py-3 font-medium text-white transition-colors active:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                저장 중...
              </span>
            ) : (
              "저장"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
